import React, { useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faLock,
  faEye,
  faEyeSlash,
  faHeadset,
  faExclamationTriangle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  Col,
  Row,
  Form,
  Card,
  Button,
  Container,
  InputGroup,
  Modal,
} from "@themesberg/react-bootstrap";
import { Link, useNavigate } from "react-router-dom";

import { Routes } from "../../routes";

// Replace this with your actual logo asset
// import BrandLogo from "../../assets/img/brand/brandlogo.jpg";
import BrandLogo from "../../assets/img/brand/Brand_Logo.png";
import api from "../api";

const LOGIN_API_URL = "/auth/login";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const usernameRegex = /^[a-z0-9_]{4,50}$/;
const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const getForgotPasswordPath = () => {
  return Routes?.ForgotPassword?.path || "/forgot-password";
};

const getSupportPath = () => {
  return Routes?.Support?.path || "#";
};

const getRedirectPathByRole = (role) => {
  const normalizedRole = String(role || "").trim().toUpperCase();

  switch (normalizedRole) {
    case "SUPER_ADMIN":
    case "ADMIN":
    case "ROLE_ADMIN":
    case "ROLE_SUPER_ADMIN":
      return Routes?.AdminDashboard?.path || Routes?.Dashboard?.path || "/admin-dashboard" || "/";

    case "USER":
    case "ROLE_USER":
    case "CHILD":
    case "ROLE_CHILD":
      return Routes?.UserDashboard?.path || "/user-dashboard" || "/";
    default:
      return Routes?.UserDashboard?.path || "/user-dashboard" || "/";
  }
};

export default function SignIn() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    loginId: "",
    password: "",
    rememberMe: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [activeModal, setActiveModal] = useState(null);
  const [modalMessage, setModalMessage] = useState("");
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [loginMode, setLoginMode] = useState("primary");

  const trimmedLoginId = useMemo(() => formData.loginId.trim(), [formData.loginId]);
  const isEmailLogin = useMemo(() => emailRegex.test(trimmedLoginId), [trimmedLoginId]);
  const isUsernameLogin = useMemo(
    () => usernameRegex.test(trimmedLoginId.toLowerCase()),
    [trimmedLoginId]
  );

  const openModal = (type, message = "") => {
    setActiveModal(type);
    setModalMessage(message);
  };

  const closeModal = () => {
    setActiveModal(null);
    setModalMessage("");
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateBeforeSubmit = () => {
    const errors = {};

    if (!trimmedLoginId) {
      errors.loginId =
        loginMode === "subid"
          ? "Username is required."
          : "Email ID is required.";
    } else if (loginMode === "primary" && !isEmailLogin) {
      errors.loginId = "Enter a valid email address.";
    } else if (loginMode === "subid" && !isUsernameLogin) {
      errors.loginId =
        "Only lowercase letters, numbers, and underscores allowed (min 4 characters).";
    }

    if (!formData.password.trim()) {
      errors.password = "Password is required.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateBeforeSubmit()) return;

    try {
      setSubmitting(true);

      const payload =
        loginMode === "subid"
          ? {
              username: trimmedLoginId.toLowerCase(),
              password: formData.password,
            }
          : {
              email: trimmedLoginId,
              password: formData.password,
            };

      // console.log("Login request:", payload);

      const response = await api.post(LOGIN_API_URL, payload);

      // console.log("Login response:", response);

      const responseData = response?.data || {};

      if (responseData?.sessionExpired) {
        openModal("sessionExpired", "Your session has expired. Please sign in again.");
        return;
      }

      if (responseData?.success === false) {
        openModal(
          "invalid",
          responseData?.message || "Invalid login ID or password."
        );
        return;
      }

      if (responseData?.status === "INACTIVE" || responseData?.accountStatus === "INACTIVE") {
        openModal("inactive", "Your account is inactive. Please contact support.");
        return;
      }

      if (responseData?.status === "BLOCKED" || responseData?.accountStatus === "BLOCKED") {
        openModal("blocked", "Your account has been blocked by the administrator.");
        return;
      }

      const token = responseData?.token;
      const tokenType = responseData?.token_type || "Bearer";
      const userData = responseData?.data || {};
      const role = userData?.user_type || responseData?.role || responseData?.userRole;
      const profilePhoto = userData?.profile_photo || responseData?.profile_photo;

      if (!token) {
        openModal("serverError", "Login succeeded but token was not returned by the server.");
        return;
      }

      sessionStorage.setItem("authToken", token);
      sessionStorage.setItem("authTokenType", tokenType);
      sessionStorage.setItem("authUser", JSON.stringify(userData));
      sessionStorage.setItem("authRole", role || "");
      sessionStorage.setItem("isAuthenticated", "true");
      sessionStorage.setItem("authProfilePhoto", profilePhoto);

      const redirectPath = getRedirectPathByRole(role);
      navigate(redirectPath, { replace: true });
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data || {};
      const apiMessage =
        data?.message || data?.error || "Network or server error. Please try again later.";

      if (status === 401) {
        openModal("invalid", "Invalid login ID or password.");
      } else if (status === 403) {
        openModal("blocked", "Your account has been blocked. Please contact support.");
      } else if (status === 423) {
        openModal("inactive", "Your account is inactive. Please contact support.");
      } else if (status === 429) {
        openModal("tooManyAttempts", "Too many failed attempts. Please reset your password.");
      } else if (status === 440) {
        openModal("sessionExpired", "Your session expired. Please sign in again.");
      } else if (error?.code === "ECONNABORTED" || !error?.response) {
        openModal("serverError", "Network or server error. Please try again later.");
      } else {
        openModal("serverError", apiMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <main className="auth-shell">
        <section className="py-5">
          <Container>
            <Row className="justify-content-center">
              <Col xs={12} md={10} lg={8} xl={6}>
                <Card className="border-0 auth-card">
                  <Card.Body className="p-4 p-md-5">
                    <div className="auth-logo-wrap">
                      <img src={BrandLogo} alt="LifeSkill Global" />
                    </div>

                    <div className="text-center mb-2">
                      <h4 className="fw-bold mb-1">Sign In</h4>
                      <p className="text-muted small mb-0">Access your courses, wallet, and network</p>
                    </div>

                    <div className="d-flex mb-4 rounded overflow-hidden border">
                      <Button
                        type="button"
                        className="flex-fill rounded-0 border-0"
                        style={{
                          background: loginMode === "primary" ? "#1D4E89" : "#ffffff",
                          color: loginMode === "primary" ? "#ffffff" : "#6b7280",
                        }}
                        onClick={() => {
                          setLoginMode("primary");
                          setFieldErrors({});
                        }}
                      >
                        Primary Account
                      </Button>
                      <Button
                        type="button"
                        className="flex-fill rounded-0 border-0"
                        style={{
                          background: loginMode === "subid" ? "#1D4E89" : "#ffffff",
                          color: loginMode === "subid" ? "#ffffff" : "#6b7280",
                        }}
                        onClick={() => {
                          setLoginMode("subid");
                          setFieldErrors({});
                        }}
                      >
                        Sub-ID Login
                      </Button>
                    </div>

                    <Form onSubmit={handleSubmit} noValidate>
                      <Form.Group className="mb-3" controlId="loginId">
                        <Form.Label className="fw-semibold">
                          {loginMode === "subid" ? "Username" : "Registered Email ID"}
                        </Form.Label>
                        <InputGroup
                          className={`flex-nowrap ${fieldErrors.loginId ? "invalid-input-group" : ""}`}
                        >
                          <InputGroup.Text className="bg-white">
                            <FontAwesomeIcon icon={faEnvelope} />
                          </InputGroup.Text>
                          <Form.Control
                            autoFocus
                            name="loginId"
                            type="text"
                            placeholder={
                              loginMode === "subid"
                                ? "e.g. institute_01"
                                : "Enter Email ID"
                            }
                            value={formData.loginId}
                            onChange={handleChange}
                            onBlur={() =>
                              setFormData((prev) => ({
                                ...prev,
                                loginId:
                                  loginMode === "subid"
                                    ? prev.loginId.trim().toLowerCase()
                                    : prev.loginId.trim(),
                              }))
                            }
                            isInvalid={!!fieldErrors.loginId}
                            autoComplete="username"
                            className="py-2 border-start-0"
                            style={{
                              boxShadow: "none",
                            }}
                          />
                        </InputGroup>
                        {loginMode === "subid" && (
                          <Form.Text className="text-muted">
                            Use the username shared with you when this ID was created.
                          </Form.Text>
                        )}
                        <Form.Control.Feedback type="invalid" className="d-block">
                          {fieldErrors.loginId}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="mb-3" controlId="password">
                        <Form.Label className="fw-semibold">Password</Form.Label>
                        {/* <InputGroup className="flex-nowrap"> */}
                        <InputGroup
                          className={`flex-nowrap ${fieldErrors.password ? "invalid-input-group" : ""}`}
                        >
                          <InputGroup.Text className="bg-white">
                            <FontAwesomeIcon icon={faLock} />
                          </InputGroup.Text>

                          <Form.Control
                            name="password"
                            type={showPassword ? "text" : "password"}
                            placeholder="Enter Password"
                            value={formData.password}
                            onChange={handleChange}
                            isInvalid={!!fieldErrors.password}
                            autoComplete="current-password"
                            className="py-2 border-start-0 border-end-0"
                            style={{
                              boxShadow: "none",
                            }}
                          />

                          <InputGroup.Text
                            className="p-0"
                            style={{
                              background: "#e9ecef",
                              minWidth: "58px",
                              justifyContent: "center",
                              borderColor: "#dee2e6",
                            }}
                          >
                            <Button
                              type="button"
                              variant="link"
                              onClick={() => setShowPassword((prev) => !prev)}
                              aria-label={showPassword ? "Hide password" : "Show password"}
                              className="w-100 h-100 p-0 text-decoration-none border-0 shadow-none d-flex align-items-center justify-content-center"
                              style={{
                                background: "transparent",
                                color: "#5f6b7a",
                                minHeight: "46px",
                              }}
                            >
                              <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                            </Button>
                          </InputGroup.Text>
                        </InputGroup>
                        <Form.Control.Feedback type="invalid" className="d-block">
                          {fieldErrors.password}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                        <Link
                          to={getForgotPasswordPath()}
                          className="fw-semibold underline-link"
                          style={{ color: "#1D4E89" }}
                        >
                          Forgot password?
                        </Link>
                      </div>

                      <div className="d-flex justify-content-center">
                        <Button
                          type="submit"
                          disabled={submitting}
                          className="px-5 py-2 fw-bold"
                          style={{
                            minWidth: "120px",
                            ...ACTIVE_BUTTON_STYLE,
                          }}
                        >
                          {submitting ? "SIGNING IN..." : "SIGN IN"}
                        </Button>
                      </div>

                      <div className="mt-3">
                        <div className="d-flex align-items-center gap-3 mb-3">
                          <div
                            style={{
                              flex: 1,
                              height: "1px",
                              background: "#e5e7eb",
                            }}
                          />
                          <span
                            className="fw-normal"
                            style={{
                              color: "#6b7280",
                              fontSize: "0.95rem",
                              letterSpacing: "0.5px",
                            }}
                          >
                            OR
                          </span>
                          <div
                            style={{
                              flex: 1,
                              height: "1px",
                              background: "#e5e7eb",
                            }}
                          />
                        </div>

                        <div className="d-flex justify-content-center">
                          <Link
                            to={Routes.LoginWithOtp.path}
                            className="btn fw-semibold underline-link px-5 py-2 otp-login-btn"
                            style={{
                              minWidth: "120px",
                              background: "#ffffff",
                              color: "#1D4E89",
                              borderRadius: "0.5rem",
                              boxShadow: "none",
                              border: "none",
                              outline: "none",
                            }}
                          >
                            Log in via OTP
                          </Link>
                        </div>
                      </div>

                      <div className="text-center mt-4">
                        <span className="text-dark">Don't have an account? </span>
                        <Link to="/register" className="fw-bold underline-link" style={{ color: "#1D4E89" }}>
                          Register
                        </Link>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="justify-content-center mt-4 mt-md-5">
              <Col xs={12} md={10} lg={8} xl={6}>
                <Card
                  className="border-0 shadow-sm rounded-4"
                  style={{ background: "#ffffff" }}
                >
                  <Card.Body className="p-4 p-md-5">
                    <h4 className="fw-bold mb-2">Need help?</h4>
                    <p className="mb-0 text-muted">
                      Contact your administrator if you can't access your account.
                    </p>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Container>
        </section>

        <Modal show={activeModal === "invalid"} onHide={closeModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
              Invalid Credentials
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalMessage || "The login ID or password you entered is incorrect."}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={closeModal}>
              Try Again
            </Button>
            <Button className="act-btn" as={Link} to={getForgotPasswordPath()} onClick={closeModal}>
              Forgot Password
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={activeModal === "inactive"} onHide={closeModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-warning" />
              Inactive Account
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalMessage || "Your account is inactive. Please contact support."}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={closeModal}>
              Close
            </Button>
            <Button className="act-btn" onClick={() => setSupportModalOpen(true)}>
              Contact Support
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={activeModal === "blocked"} onHide={closeModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
              Blocked Account
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalMessage || "Your account has been blocked. Please contact support."}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={closeModal}>
              Close
            </Button>
            <Button className="act-btn" onClick={() => setSupportModalOpen(true)}>
              Contact Support
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={activeModal === "tooManyAttempts"} onHide={closeModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-warning" />
              Too Many Failed Attempts
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalMessage || "Too many failed attempts detected. Reset your password to continue."}
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={closeModal}>
              Close
            </Button>
            <Button
              className="act-btn"
              as={Link}
              to={getForgotPasswordPath()}
              onClick={closeModal}
            >
              Reset Password
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={activeModal === "sessionExpired"} onHide={closeModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-info" />
              Session Expired
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalMessage || "Your session expired. Please sign in again."}
          </Modal.Body>
          <Modal.Footer>
            <Button className="act-btn" onClick={closeModal}>
              Back to Login
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={activeModal === "serverError"} onHide={closeModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
              Server / Network Error
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalMessage || "Unable to reach the server. Please try again later."}
          </Modal.Body>
          <Modal.Footer>
            <Button className="act-btn" onClick={closeModal}>
              Try Again
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={supportModalOpen} onHide={() => setSupportModalOpen(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>
              <FontAwesomeIcon icon={faHeadset} className="me-2" />
              Contact Support
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-2">Use your support details here.</p>
            <p className="mb-0 text-muted">
              Example: support@yourcompany.com | +91-XXXXXXXXXX
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button onClick={() => setSupportModalOpen(false)}>
              Close
            </Button>
            <Button
              className="act-btn"
              as={Link}
              to={getSupportPath()}
              onClick={() => setSupportModalOpen(false)}
            >
              Open Support
            </Button>
          </Modal.Footer>
        </Modal>
      </main>

      <style>
        {`
.otp-login-btn,
.otp-login-btn:hover,
.otp-login-btn:focus,
.otp-login-btn:active {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  background: #ffffff !important;
}
`}
      </style>
    </>
  );
}